import type { Workspace, WorkspaceState } from './types';

import { match } from 'path-to-regexp';

import { paths } from 'src/routes/paths';

import * as ActionTypes from 'src/state/action-types';

import { loadWorkspaces } from './actions';

export const initialState: WorkspaceState = {
  currentWorkspaceId: 'private',
  currentWorkspace: null,
  workspaces: [
    {
      id: 'private',
      name: 'My Workspace',
      role: 'owner',
      logo: '/assets/icons/workspaces/logo-1.webp',
    },
  ],
  isLoading: false,
};

const workspace_matcher = match(paths.dashboard.folder(':workspaceId', ':folderId'));

// Helper function to find workspace by ID
function findWorkspaceById(workspaces: Workspace[], workspaceId: string): Workspace | null {
  return workspaces.find((w) => w.id === workspaceId) || null;
}

// Helper function to get initial workspace from localStorage
function getInitialWorkspaceId(): string | null {
  try {
    return localStorage.getItem('lastWorkspaceId');
  } catch {
    return null;
  }
}

export default function workspaceReducer(
  state: WorkspaceState = initialState,
  action: ActionTypes.ActionTypes
): WorkspaceState {
  switch (action.type) {
    case ActionTypes.WORKSPACE_WORKSPACES_LOADED: {
      // Business logic: Add private workspace and select initial workspace
      const privateWorkspace: Workspace = {
        id: 'private',
        name: 'My Workspace',
        role: 'owner',
        logo: '/assets/icons/workspaces/logo-1.webp',
      };

      const allWorkspaces = [privateWorkspace, ...action.payload.workspaces];

      // Business rule: Select workspace from localStorage or default to first
      const lastWorkspaceId = getInitialWorkspaceId();
      const initialWorkspace = lastWorkspaceId
        ? findWorkspaceById(allWorkspaces, lastWorkspaceId) || allWorkspaces[0]
        : allWorkspaces[0];

      return {
        ...state,
        workspaces: allWorkspaces,
        currentWorkspace: initialWorkspace,
        currentWorkspaceId: initialWorkspace.id,
      };
    }

    case ActionTypes.WORKSPACE_WORKSPACE_SELECTED: {
      // Business logic: Find and set the selected workspace
      const { workspaceId } = action.payload;
      const workspace = findWorkspaceById(state.workspaces, workspaceId);

      // Business rule: Only update if workspace exists
      if (!workspace) {
        return state;
      }

      // Business rule: Skip update if already selected
      if (state.currentWorkspace?.id === workspaceId) {
        return state;
      }

      return {
        ...state,
        currentWorkspace: workspace,
        currentWorkspaceId: workspace.id,
      };
    }

    case ActionTypes.WORKSPACE_WORKSPACE_CREATED: {
      // Business logic: Add new workspace and select it
      const { workspace } = action.payload;

      // Business rule: Check if workspace already exists (deduplication)
      const existingWorkspace = findWorkspaceById(state.workspaces, workspace.id);
      if (existingWorkspace) {
        // Already exists, just select it
        return {
          ...state,
          currentWorkspace: workspace,
          currentWorkspaceId: workspace.id,
        };
      }

      // Add to list and select it
      return {
        ...state,
        workspaces: [...state.workspaces, workspace],
        currentWorkspace: workspace,
        currentWorkspaceId: workspace.id,
      };
    }

    case ActionTypes.WORKSPACE_WORKSPACE_UPDATED: {
      // Business logic: Update workspace in list
      const { workspace } = action.payload;

      // Update in workspaces list
      const updatedWorkspaces = state.workspaces.map((w) =>
        w.id === workspace.id ? workspace : w
      );

      // Update current workspace if it's the one being updated
      const updatedCurrentWorkspace =
        state.currentWorkspace?.id === workspace.id ? workspace : state.currentWorkspace;

      return {
        ...state,
        workspaces: updatedWorkspaces,
        currentWorkspace: updatedCurrentWorkspace,
      };
    }

    case ActionTypes.WORKSPACE_LOADING_CHANGED:
      return {
        ...state,
        isLoading: action.payload.isLoading,
      };

    case ActionTypes.APP_NAVIGATED: {
      // Business logic: Update current workspace ID from URL
      const matchResult = workspace_matcher(action.payload.path);
      if (matchResult && matchResult.params && matchResult.params.workspaceId) {
        const { workspaceId } = matchResult.params;

        // Skip if already at this workspace
        if (state.currentWorkspaceId === workspaceId) {
          return state;
        }

        // Update currentWorkspaceId and currentWorkspace if available
        const workspace = findWorkspaceById(state.workspaces, workspaceId as string);

        return {
          ...state,
          currentWorkspaceId: workspaceId as string,
          // Only update currentWorkspace if we found it in the list
          ...(workspace && { currentWorkspace: workspace }),
        };
      }
      return state;
    }

    default:
      // Handle async thunk actions
      if (loadWorkspaces.pending.match(action)) {
        return {
          ...state,
          isLoading: true,
        };
      }

      if (loadWorkspaces.fulfilled.match(action)) {
        // Business logic: Add private workspace and select initial workspace
        const privateWorkspace: Workspace = {
          id: 'private',
          name: 'My Workspace',
          role: 'owner',
          logo: '/assets/icons/workspaces/logo-1.webp',
        };

        const allWorkspaces = [privateWorkspace, ...action.payload];

        // Business rule: Select workspace from localStorage or default to first
        const lastWorkspaceId = getInitialWorkspaceId();
        const initialWorkspace = lastWorkspaceId
          ? findWorkspaceById(allWorkspaces, lastWorkspaceId) || allWorkspaces[0]
          : allWorkspaces[0];

        return {
          ...state,
          workspaces: allWorkspaces,
          currentWorkspace: initialWorkspace,
          currentWorkspaceId: initialWorkspace.id,
          isLoading: false,
        };
      }

      if (loadWorkspaces.rejected.match(action)) {
        return {
          ...state,
          isLoading: false,
        };
      }

      return state;
  }
}
